/*
 * ตาราง on the page: a fabric.Group carrying its data as `table` (src/table/tableModel.ts). What is inside the group —
 * a clear box the size of the table, one rectangle per cell (fill + grid line) and one Thai text box per cell with
 * text — is drawn from the data every time it changes, so the picture, the print PDF (vector, src/print/pdf.ts
 * drawVector walks groups) and the document PDF (copyable text) need nothing new.
 *
 * An app from before tables (the server refuses its saves over a design with a table: D1 has_tables, 0027) sees a
 * plain group and still shows it as it was.
 */
import { fabric } from 'fabric';
import { EXTRA_PROPS } from '../editor/editor';
import { fontNeeds, loadFonts } from '../fonts/load';
import { ThaiTextbox } from '../thai/ThaiTextbox';
import { layoutTable, type Measure } from './tableLayout';
import { TABLE_COL_MIN, TABLE_LINE_HEIGHT, TABLE_SIDE_MAX, type TableData } from './tableModel';

export type TableGroup = fabric.Group & { table: TableData };
export const isTable = (o: fabric.Object | null | undefined): o is TableGroup =>
  !!o && o.type === 'group' && !!(o as { table?: unknown }).table && typeof (o as { table?: unknown }).table === 'object';

/** the fonts (and weights) a table's text needs */
export function tableFontNeeds(t: TableData) {
  const weights = new Set<number>([400]);
  for (const row of t.cells) for (const c of row) if (c.b) weights.add(700);
  return fontNeeds([...weights].map(w => ({ type: 'thaiTextbox', fontFamily: t.font, fontWeight: w })));
}

/** loads the table's font first (text measured with a stand-in would be laid out wrong); the reason when it cannot */
export async function loadTableFonts(t: TableData): Promise<string | null> {
  const failed = await loadFonts(tableFontNeeds(t));
  fabric.util.clearFabricFontCache(t.font);
  return failed.length ? `โหลดฟอนต์ ${failed.join(', ')} ไม่สำเร็จ ตรวจอินเทอร์เน็ตแล้วลองใหม่ (ตารางยังไม่ได้เปลี่ยน)` : null;
}

/** heights from a real Thai text box (the same wrapping and font as the one drawn) */
export function textMeasure(): Measure {
  const seen = new Map<string, number>();
  return (text, o) => {
    const key = `${o.font}|${o.weight}|${o.size}|${o.width}|${o.align}|${text}`;
    let h = seen.get(key);
    if (h === undefined) {
      const box = new ThaiTextbox(text, { width: o.width, fontSize: o.size, fontFamily: o.font, fontWeight: o.weight, textAlign: o.align, lineHeight: TABLE_LINE_HEIGHT });
      h = box.height ?? 0;
      seen.set(key, h);
    }
    return h;
  };
}

const PART = { selectable: false, evented: false, hasControls: false, objectCaching: false } as const;

/** the group's insides for this table, page px from the table's top-left */
export function tableParts(t: TableData, measure: Measure = textMeasure()): { parts: fabric.Object[]; w: number; h: number } {
  const lay = layoutTable(t, measure);
  const half = t.lw / 2;
  // a clear box the size of the table (and its outer lines): the group is always exactly this big, whatever is drawn
  const parts: fabric.Object[] = [new fabric.Rect({ ...PART, left: -half, top: -half, width: lay.w + t.lw, height: lay.h + t.lw, fill: '', strokeWidth: 0 })];
  for (const c of lay.cells) {
    if (!c.cell.bg && t.lw <= 0) continue;
    // the line runs along the cell's edge (half inside, half outside), the same as the neighbour's: one line
    parts.push(new fabric.Rect({
      ...PART, left: c.x - half, top: c.y - half, width: c.w, height: c.h,
      fill: c.cell.bg ?? '', stroke: t.lw > 0 ? t.line : undefined, strokeWidth: t.lw, strokeUniform: false
    }));
  }
  for (const c of lay.cells) {
    if (!c.text) continue;
    const o = c.text.opts;
    parts.push(new ThaiTextbox(c.cell.t, {
      ...PART, left: c.text.x, top: c.text.y, width: o.width, fontSize: o.size, fontFamily: o.font, fontWeight: o.weight,
      textAlign: o.align, lineHeight: TABLE_LINE_HEIGHT, fill: c.cell.c ?? t.color
    }));
  }
  return { parts, w: lay.w + t.lw, h: lay.h + t.lw };
}

/** a new table on the page (its fonts loaded first: loadTableFonts) */
export function makeTable(t: TableData): TableGroup {
  const { parts } = tableParts(t);
  const g = new fabric.Group(parts, { subTargetCheck: false }) as TableGroup;
  g.table = t;
  return g;
}

/**
 * The same group drawn again from new data, in place: it stays the same object (still chosen, same layer, same group
 * membership, undo sees one change), its top-left corner stays where it is.
 */
export function rebuildTable(g: TableGroup, t: TableData, measure?: Measure) {
  const { parts } = tableParts(t, measure);
  // fabric works out the parts' places around their middle; they are then moved into this group as they are
  const made = new fabric.Group(parts);
  const inner = made.getObjects();
  (made as unknown as { _objects: fabric.Object[] })._objects = [];
  for (const o of inner) o.group = g;
  (g as unknown as { _objects: fabric.Object[] })._objects = inner;
  g.set({ width: made.width, height: made.height });
  g.table = t;
  g.dirty = true;
  g.setCoords();
}

/**
 * Pulled bigger or smaller with the handles: the columns and rows take the new size and the text keeps its size (a
 * table is not a picture — stretched letters would not match the font). The height still follows the text.
 */
export function bakeScale(g: TableGroup): boolean {
  const sx = g.scaleX ?? 1, sy = g.scaleY ?? 1;
  if (Math.abs(sx - 1) < 1e-6 && Math.abs(sy - 1) < 1e-6) return false;
  const t = g.table, measure = textMeasure();
  const fit = (v: number, lo: number) => Math.min(TABLE_SIDE_MAX, Math.max(lo, Math.round(v * 10) / 10));
  const cols = t.cols.map(w => fit(w * sx, TABLE_COL_MIN));
  // taller/shorter: each row's height as it was drawn, times the pull (rows still grow to fit their text)
  const rows = Math.abs(sy - 1) < 1e-6 ? t.rows : layoutTable(t, measure).rowH.map(h => fit(h * sy, 0));
  g.set({ scaleX: 1, scaleY: 1 });
  rebuildTable(g, { ...t, cols, rows }, measure);
  return true;
}

/**
 * A table on a page that is not open (saved JSON): its insides drawn again from new data (เอกสาร: another language —
 * src/ui/docFill.ts), so pictures/PDF made from that JSON show the new words. Its place, size and turn are kept; its
 * fonts must be loaded first (loadTableFonts).
 */
export function tableJson(o: Record<string, unknown>, t: TableData): Record<string, unknown> {
  const made = makeTable(t).toObject(EXTRA_PROPS) as Record<string, unknown>;
  return { ...o, table: t, objects: made.objects, width: made.width, height: made.height };
}
